// BroadcastChannelAdapter — Default implementation for cross-tab communication
// Injected into VNInvestCharts to avoid direct BroadcastChannel usage
// ============================================
// Default BroadcastChannel Adapter
// ============================================
export const createDefaultBroadcastChannelAdapter = (workspaceId, clockAdapter) => {
    let channel = null;
    const handlers = new Set();
    let isInitialized = false;
    const getTime = clockAdapter?.now ?? (() => {
        if (typeof performance !== 'undefined' && performance.now) {
            return performance.now();
        }
        return Date.now();
    });
    const generateTabId = () => {
        // Use timestamp + random for uniqueness
        return `tab-${getTime().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    };
    const initialize = () => {
        if (isInitialized || typeof BroadcastChannel === 'undefined')
            return;
        try {
            channel = new BroadcastChannel(`vninvestcharts-sync-${workspaceId}`);
            channel.onmessage = (event) => {
                const message = event.data;
                // Don't echo our own messages
                if (message.sourceTabId === currentTabId)
                    return;
                handlers.forEach(handler => handler(message));
            };
            isInitialized = true;
        }
        catch (e) {
            console.warn('BroadcastChannelAdapter: Failed to initialize', e);
        }
    };
    const currentTabId = generateTabId();
    return {
        postMessage: (message) => {
            initialize();
            if (channel) {
                channel.postMessage({
                    ...message,
                    sourceTabId: currentTabId,
                    timestamp: getTime(),
                });
            }
        },
        onMessage: (handler) => {
            initialize();
            handlers.add(handler);
            return () => handlers.delete(handler);
        },
        close: () => {
            channel?.close();
            channel = null;
            isInitialized = false;
            handlers.clear();
        },
        isAvailable: () => typeof BroadcastChannel !== 'undefined',
    };
};
// ============================================
// No-op Adapter (for environments without BroadcastChannel)
// ============================================
export const createNoopBroadcastChannelAdapter = () => ({
    postMessage: () => { },
    onMessage: () => () => { },
    close: () => { },
    isAvailable: () => false,
});
export const createMockBroadcastChannelAdapter = () => {
    const receivedMessages = [];
    const sentMessages = [];
    const handlers = new Set();
    const adapter = {
        postMessage: (message) => {
            sentMessages.push(message);
        },
        onMessage: (handler) => {
            handlers.add(handler);
            return () => handlers.delete(handler);
        },
        close: () => {
            handlers.clear();
        },
        isAvailable: () => true,
        // Test utilities
        receivedMessages,
        sentMessages,
        simulateMessage: (message) => {
            receivedMessages.push(message);
            handlers.forEach(handler => handler(message));
        },
        resetCalls: () => {
            receivedMessages.length = 0;
            sentMessages.length = 0;
        },
    };
    return adapter;
};
// ============================================
// localStorage Broadcast Adapter (fallback for Safari Private / iframe sandbox)
// ============================================
export const createLocalStorageBroadcastAdapter = (workspaceId, clockAdapter) => {
    const key = `vninvestcharts-sync-${workspaceId}`;
    const handlers = new Set();
    const tabId = `tab-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    let pollingTimer = null;
    let lastPollTime = 0;
    const getTime = clockAdapter?.now ?? (() => Date.now());
    const onStorageEvent = (event) => {
        if (event.key !== key || !event.newValue)
            return;
        try {
            const message = JSON.parse(event.newValue);
            if (message.sourceTabId === tabId)
                return;
            handlers.forEach(handler => handler(message));
        }
        catch { /* malformed message */ }
    };
    const startPolling = () => {
        if (pollingTimer)
            return;
        pollingTimer = setInterval(() => {
            try {
                const raw = localStorage.getItem(key);
                if (!raw)
                    return;
                const parsed = JSON.parse(raw);
                if (parsed.timestamp > lastPollTime && parsed.sourceTabId !== tabId) {
                    lastPollTime = parsed.timestamp;
                    handlers.forEach(handler => handler(parsed));
                }
            }
            catch { /* ignore */ }
        }, 500);
    };
    if (typeof window !== 'undefined' && window.addEventListener) {
        window.addEventListener('storage', onStorageEvent);
    }
    startPolling();
    return {
        postMessage: (message) => {
            try {
                const outgoing = { ...message, sourceTabId: tabId, timestamp: getTime() };
                localStorage.setItem(key, JSON.stringify(outgoing));
                lastPollTime = outgoing.timestamp;
            }
            catch { /* quota exceeded or private mode */ }
        },
        onMessage: (handler) => {
            handlers.add(handler);
            return () => handlers.delete(handler);
        },
        close: () => {
            if (pollingTimer) {
                clearInterval(pollingTimer);
                pollingTimer = null;
            }
            if (typeof window !== 'undefined' && window.removeEventListener) {
                window.removeEventListener('storage', onStorageEvent);
            }
            handlers.clear();
        },
        isAvailable: () => {
            try {
                const test = '__vninvestcharts_test__';
                localStorage.setItem(test, '1');
                localStorage.removeItem(test);
                return true;
            }
            catch {
                return false;
            }
        },
    };
};
// ============================================
// Factory for creating appropriate adapter based on environment
// ============================================
export const createBroadcastChannelAdapter = (workspaceId, clockAdapter) => {
    if (typeof BroadcastChannel !== 'undefined') {
        return createDefaultBroadcastChannelAdapter(workspaceId, clockAdapter);
    }
    const lsAdapter = createLocalStorageBroadcastAdapter(workspaceId, clockAdapter);
    if (lsAdapter.isAvailable())
        return lsAdapter;
    return createNoopBroadcastChannelAdapter();
};
